import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { renderHistoryService } from '../../services/api';
import { RenderHistoryResponse, RenderHistoryItem } from '../../types';

const RenderHistory: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState<RenderHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchRenderHistory = async (page: number = 1) => {
    if (!templateId) return;
    
    try {
      setLoading(true);
      const response = await renderHistoryService.getTemplateRenderHistory(templateId, page, 20);
      setHistoryData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Render history yüklenirken hata oluştu');
      console.error('Render history fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRenderHistory(currentPage);
  }, [templateId, currentPage]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const copyImageUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    // TODO: Add toast notification
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-red-800 font-medium">Hata</h3>
            <p className="text-red-600 mt-2">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Geri Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!historyData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate(-1)}
                className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
              >
                ← Geri Dön
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                Render Geçmişi
              </h1>
              <p className="text-gray-600 mt-2">
                <span className="font-medium">{historyData.templateName}</span> template'i için render geçmişi
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {historyData.pagination.totalCount}
              </div>
              <div className="text-sm text-gray-500">Toplam Render</div>
            </div>
          </div>
        </div>

        {/* Render History List */}
        {historyData.renders.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-gray-400 text-6xl mb-4">🖼️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Henüz render edilmiş resim yok
            </h3>
            <p className="text-gray-500">
              Bu template henüz render edilmemiş. API'yi kullanarak resim oluşturmaya başlayın.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {historyData.renders.map((render: RenderHistoryItem) => (
              <div key={render._id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    {/* Left side - Image and basic info */}
                    <div className="flex space-x-4">
                      <div className="flex-shrink-0">
                        <img
                          src={render.imageUrl}
                          alt="Rendered image"
                          className="w-20 h-20 object-cover rounded-lg border"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAzMkw0MCA0OEw1NiAzMkw2NCA0MEw2NCA1NkgxNlY0MEwyNCAzMloiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            render.status === 'success' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {render.status === 'success' ? '✅ Başarılı' : '❌ Hatalı'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatDate(render.createdAt)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>📏 Boyut: {formatFileSize(render.imageSize)}</div>
                          <div>⏱️ Süre: {formatDuration(render.renderDuration)}</div>
                          <div>👤 Kullanıcı: {render.userId.email}</div>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Actions */}
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={() => window.open(render.imageUrl, '_blank')}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        🔗 Aç
                      </button>
                      <button
                        onClick={() => copyImageUrl(render.imageUrl)}
                        className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                      >
                        📋 URL Kopyala
                      </button>
                    </div>
                  </div>

                  {/* Render Data */}
                  {Object.keys(render.renderData).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Render Verileri:</h4>
                      <div className="bg-gray-50 rounded p-3">
                        <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                          {JSON.stringify(render.renderData, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {render.errorMessage && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-red-900 mb-2">Hata Mesajı:</h4>
                      <div className="bg-red-50 rounded p-3">
                        <p className="text-sm text-red-600">{render.errorMessage}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {historyData.pagination.totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!historyData.pagination.hasPrev}
                className="px-3 py-2 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                ← Önceki
              </button>
              
              <div className="flex space-x-1">
                {[...Array(historyData.pagination.totalPages)].map((_, i) => {
                  const page = i + 1;
                  const isCurrentPage = page === currentPage;
                  
                  // Show first, last, current, and adjacent pages
                  if (
                    page === 1 || 
                    page === historyData.pagination.totalPages || 
                    Math.abs(page - currentPage) <= 1
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 border text-sm rounded ${
                          isCurrentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 || 
                    page === currentPage + 2
                  ) {
                    return <span key={page} className="px-2 py-2 text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!historyData.pagination.hasNext}
                className="px-3 py-2 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Sonraki →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RenderHistory;
